import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { eventPointOfInterestSchema } from '../../../schemas/event-point-of-interest'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { MediaField } from 'domains/media/components/media-field'
import { LayoutFullWidthPage } from 'layouts/full-width-page'
import { Description, Image, Title, Subtitle } from 'components/ui/list-item-label'

import { GetEventPointOfInterestRequest } from 'generated/mos/org/getty/audioaccess'
import { EventPointOfInterest } from 'generated/mos/org/getty/audio'
import { Media } from 'generated/mos/media'
import { UpdateEventPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'
import { Language } from 'generated/mos/i18n'

import { bffEndpoint } from 'helpers/bff'
import { pick, areRefValuesEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags } from 'helpers/schema'
import { defaultLanguage, isDefaultLanguage, parseLanguage } from 'helpers/i18n'
import { getResolvedValue } from 'helpers/linkables'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getEventPointOfInterestSubnav } from '../subnav'

import { LANGUAGES, LanguageCode } from '../../../constants'
import { Input } from 'components/ui/input'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetEventPointOfInterestRequest' | 'gettyUpdateEventPointOfInterestRequest' | 'gettyUpdateEventPointOfInterestReset'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'eventPointOfInterestUpdate'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly pointOfInterestId: string;
  readonly moodId: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  teaserText: string | undefined;
  content: string | undefined;
  additionalInfo: string | undefined;
  deepDiveLabel: string | undefined;
  deepDiveUrl: string | undefined;
  audio: Media.Ref | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);

`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const StopItem = styled.div`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props

  const updateFormBagAudio = (entity: Media.Entity | undefined) => {
    if(formBag) {
      setFormBag({
        ...formBag,
        values: {
          ...formBag.values,
          audio: entity && entity.ref || undefined
        }
      })
    }
  }

  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            component="textarea"
            label="Teaser Text"
            field="teaserText"
            isDisabled={isDefaultLanguage(language)}
          />
          <Input
            component="textarea"
            label="Content"
            field="content"
            isDisabled={isDefaultLanguage(language)}
          />
          <Input
            component="textarea"
            label="Additional Info"
            field="additionalInfo"
            isDisabled={isDefaultLanguage(language)}
          />
          <Input
            label="Deep Dive Label"
            field="deepDiveLabel"
            isDisabled={isDefaultLanguage(language)}
          />
          <Input
            label="Deep Dive URL"
            field="deepDiveUrl"
            isDisabled={isDefaultLanguage(language)}
          />
          <MediaField
            kind="MEDIA_KIND_AUDIO"
            mediaRef={formBag.values.audio}
            onUpdate={(entity) => updateFormBagAudio(entity)}
            label="Audio"
            language={language}
            isDisabled={isDefaultLanguage(language)}
          />
        </Fieldset>
      </FormData>
    </Panel>
  ) : null
}

const EventPointOfInterestTranslationsView = (props: Props) => {
  const {
    gettyGetEventPointOfInterestRequest ,
    gettyUpdateEventPointOfInterestRequest,
    historyPush,
    projectId,
    pointOfInterestId,
    eventPointOfInterestUpdate,
    toastNotification,
    moodId,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);

  const backLocation = `/project/${projectId}/mood/${moodId}`;

  const pointOfInterestUpdateData = statusSelector.data(eventPointOfInterestUpdate);
  const pointOfInterest = pointOfInterestUpdateData && pointOfInterestUpdateData.eventPointOfInterest;

  const hasSubmitted = useRef(false)
  const hostName = bffEndpoint(window.location.hostname)

  // load initial data
  useEffect(() => {
    if (eventPointOfInterestUpdate.status === Status.Idle || (pointOfInterest && EventPointOfInterest.mustRef(pointOfInterest.ref).id !== pointOfInterestId)) {
      gettyGetEventPointOfInterestRequest({
        type: GetEventPointOfInterestRequest.refName,
        eventPointOfInterestRef: {
          typename: 'mos.org.getty.audio.EventPointOfInterest',
          id: pointOfInterestId,
        }
      })
    }
  })


  useEffect(() => {

    // only proceed if stop data is available and status is ready
    if (!pointOfInterest || eventPointOfInterestUpdate.status !== Status.Ready) return;

    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {

          const teaserText = pointOfInterest.teaserText ? getResolvedValue(eventPointOfInterestSchema.teaserText.read(pointOfInterest.teaserText, curr), []) : ''
          const content = pointOfInterest.content ? getResolvedValue(eventPointOfInterestSchema.content.read(pointOfInterest.content, curr), []) : ''
          const additionalInfo = pointOfInterest.additionalInfo ? getResolvedValue(eventPointOfInterestSchema.additionalInfo.read(pointOfInterest.additionalInfo, curr), []) : ''
          const deepDiveLabel = pointOfInterest.deepDiveLabel ? getResolvedValue(eventPointOfInterestSchema.deepDiveLabel.read(pointOfInterest.deepDiveLabel, curr), []) : ''
          const deepDiveUrl = pointOfInterest.deepDiveUrl ? getResolvedValue(eventPointOfInterestSchema.deepDiveUrl.read(pointOfInterest.deepDiveUrl, curr), []) : ''
          const audio = eventPointOfInterestSchema.audio.read(pointOfInterest.audio, curr) as Media.Ref | undefined

          return {
            ...acc,
            [curr.languageCode]: createFormBag({
              teaserText,
              content,
              additionalInfo,
              deepDiveLabel,
              deepDiveUrl,
              audio,
            })
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }

    const initValues = getInitialValues()
    setInitialValues(initValues)
    setFormBags(initValues)

  }, [pointOfInterest, eventPointOfInterestUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps


  // if the data has been submitted, trigger success toast.
  useEffect(() => {
    if (eventPointOfInterestUpdate.status === Status.Ready && hasSubmitted.current) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      hasSubmitted.current = false
    }
  },[hasSubmitted, eventPointOfInterestUpdate.status, toastNotification])

  /**
   * Determine if current form state is dirty
   * Iterate through through the form values for each translation language
   * and return true as soon as it finds a value that has been changed
   * @returns boolean
   */
  const areValuesModified = () => {
    // don't continue unless initial values and formbag values exist
    if (!initialValues || !formBags) return;

    for (const value of LANGUAGES) {
      const languageCode: LanguageCode = value.languageCode as LanguageCode

      const values = {
        initial: {...initialValues[languageCode].values},
        current: {...formBags[languageCode].values}
      }

      if (
        values.initial.teaserText !== values.current.teaserText ||
        values.initial.content !== values.current.content ||
        values.initial.additionalInfo !== values.current.additionalInfo ||
        values.initial.deepDiveLabel !== values.current.deepDiveLabel ||
        values.initial.deepDiveUrl !== values.current.deepDiveUrl ||
        !areRefValuesEqual(values.initial.audio, values.current.audio)
      ) return true;
    }

    return false;
  }

  const modified = areValuesModified()


  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true;
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm);
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  /**
   * The default language values for each field are resolved to string in the form fields
   * but they could really be be entity references. Before saving updated translation strings
   * we need to copy the original values for the default language back into the array
   * of translated values so that we don't accidentally overwrite entity reference
   * values with resolved strings in the form data
   * @param field name of the field to operate on
   * @param translatedValues the function returned by translateValues for field lookup
   * @param stop Stop.Entity which includes the original values we need to retrieve
   * @returns
   */
  const insertOriginalDefaultLanguageValues = <K extends keyof EventPointOfInterest.Entity>(
    field: K,
    translatedValues: (key: K) => any[],
    pointOfInterest: EventPointOfInterest.Entity
  ) => {
    // Look in the raw stop data received from the server to
    // get the default language's unmodified  values for the field
    const defaultLanguageValues = eventPointOfInterestSchema[field].read(pointOfInterest[field], defaultLanguage());

    // get an array of translated values for the specific field we're interested in
    // from the formbag data (values entered by the user in the UI)
    const translations = translatedValues(field);

    // find the index for the default language in the translated values from the form
    // ** these are the values we want to replace **
    const index = translations.findIndex(lang => isDefaultLanguage(lang.language))

    // if the original data contained values for the default language
    // smoosh them back into th translated values data
    // or if they somehow aren't preent in the translated values, append them to the end
    if (defaultLanguageValues && index >=0) {
      const insertAt = index >= 0 ? index : translations.length;
      translations[index].stringValue = defaultLanguageValues
    }

    // return the array containing the form data and the merged original values for default language
    return translations;
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, pointOfInterest: EventPointOfInterest.Entity) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    const translatedValues = translateValues<EventPointOfInterest.Entity, FormValues, LanguageCode>(eventPointOfInterestSchema, pointOfInterest, LANGUAGES, formBags)

    const teaserText = insertOriginalDefaultLanguageValues('teaserText', translatedValues, pointOfInterest);
    const content = insertOriginalDefaultLanguageValues('content', translatedValues, pointOfInterest);
    const additionalInfo = insertOriginalDefaultLanguageValues('additionalInfo', translatedValues, pointOfInterest);
    const deepDiveLabel = insertOriginalDefaultLanguageValues('deepDiveLabel', translatedValues, pointOfInterest);
    const deepDiveUrl = insertOriginalDefaultLanguageValues('deepDiveUrl', translatedValues, pointOfInterest);

    // Update Stop with changes.
    gettyUpdateEventPointOfInterestRequest({
      ...UpdateEventPointOfInterestRequest.defaults,
      eventPointOfInterestRef: EventPointOfInterest.mustRef(pointOfInterest.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['teaserText', 'content', 'additionalInfo', 'deepDiveLabel', 'deepDiveUrl', 'audio'],
      },
      teaserText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: teaserText
      },
      content: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: content
      },
      additionalInfo: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: additionalInfo
      },
      deepDiveLabel: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: deepDiveLabel
      },
      deepDiveUrl: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: deepDiveUrl
      },
      audio: {
        type: "mos.translatable.TranslatableMediaValue",
        translatedValues: translatedValues('audio')
      },
    })

    hasSubmitted.current = true

    return false;
  };

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if (!formBags) return;
    setFormBags({ ...formBags, [languageCode]: bag })
  }

  const title = pointOfInterest && pointOfInterest.event ? pointOfInterest.event.title : ''
  const imagePath = pointOfInterest && pointOfInterest.event ? pointOfInterest.event.imageSmallSquareUrl : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate stop "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getEventPointOfInterestSubnav(projectId, pointOfInterestId, moodId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={title}
        content={() => (
          <StatusGuard status={eventPointOfInterestUpdate} mode="form">
            <Scroll>
              <Panels>
                {formBags && LANGUAGES.map((value) => {
                  const languageCode: LanguageCode = value.languageCode as LanguageCode
                  return (
                    <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                  )
                })}
              </Panels>
            </Scroll>
          </StatusGuard>
        )}
        footer={() => formBags && pointOfInterest && (
          <FooterEditEntity
            entity={eventPointOfInterestUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, pointOfInterest);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const EventPointOfInterestTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'eventPointOfInterestUpdate'),
  {
    ...pick(actionCreators, 'gettyGetEventPointOfInterestRequest', 'gettyUpdateEventPointOfInterestRequest', 'gettyUpdateEventPointOfInterestReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(EventPointOfInterestTranslationsView);
